import { t } from '@/i18n/i18n'
import {
  type ApplicationConfig,
  ApplicationConfigService,
} from '@/shared/service/applicationConfig.service'
import { ErrorService } from '@/shared/service/errorService'
import { FilesService } from '@/shared/service/files.service'
import { SnackbarService } from '@/shared/snackbar/snackbar.service'
import { create } from '@/shared/utils/zustandVue'

import { useAppConfigStore } from '../../shared/store/app-config.store'
import { InternalType } from '../../shared/types/files.type'

class SplashScreenState {
  applicationCfg: ApplicationConfig | null = null
  cropSplashImage: string | null = null
  /**
   * Crop of the saved image, emitted by CropImg once it has drawn it; used to
   * detect unsaved changes. `undefined` while that first crop is awaited
   * (CropImg emits nothing when there is no saved image: the guard is then
   * `null`).
   */
  guardCropImg: string | null | undefined = undefined
  loading = true
}

/** Whether the crop differs from the saved splash image. */
export function computeIsSplashDirty(
  state: Pick<SplashScreenState, 'cropSplashImage' | 'guardCropImg'>,
): boolean {
  if (state.guardCropImg === undefined) return state.cropSplashImage != null
  return state.guardCropImg !== state.cropSplashImage
}

type SplashScreenStore = SplashScreenState & {
  setCropSplashImage: (payload: string | null) => void
  init: () => Promise<void>
  save: () => Promise<void>
}

/**
 * Splash screen store — idiomatic zustand-vue.
 *
 * Manages the splash-screen editor: the cropped image awaiting save, the
 * guard snapshot used to detect unsaved changes on route-leave, and the
 * loaded `ApplicationConfig`. The `init` action loads the config (and
 * mirrors it into the app-config store); `save`
 * uploads the cropped image and persists the updated config.
 */
export const useSplashScreenStore = create<SplashScreenStore>((set, get) => ({
  ...new SplashScreenState(),
  setCropSplashImage: (payload) => {
    if (get().guardCropImg === undefined) {
      set({ cropSplashImage: payload, guardCropImg: payload })
    } else {
      set({ cropSplashImage: payload })
    }
  },
  async init() {
    try {
      set({ loading: true })
      const applicationCfg = await ApplicationConfigService.get()
      set({
        applicationCfg,
        cropSplashImage: null,
        guardCropImg: applicationCfg.splashImage != null ? undefined : null,
      })
      useAppConfigStore.getState().setAppConfig(applicationCfg)
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      set({ loading: false })
    }
  },
  async save() {
    try {
      set({ loading: true })
      const { cropSplashImage, applicationCfg } = get()
      if (cropSplashImage == null || applicationCfg == null) {
        return
      }
      const { filename } = await FilesService.uploadImage({
        image: cropSplashImage,
        internalType: InternalType.SPLASHSCREEN,
      })
      const updatedCfg =
        await ApplicationConfigService.updateSplashImage(filename)
      // CropImg reloads the saved image and emits its crop: the new guard
      set({
        cropSplashImage: null,
        guardCropImg: updatedCfg.splashImage != null ? undefined : null,
        applicationCfg: updatedCfg,
      })
      useAppConfigStore.getState().setAppConfig(updatedCfg)
      SnackbarService.info(t('APPLICATION_CFG.SAVE'))
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      set({ loading: false })
    }
  },
}))
